// @flow
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {useForm} from 'react-hook-form';

import {Snackbar, Typography} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import makeStyles from '@material-ui/core/styles/makeStyles';

// import deadline1 from '../../../assets/img/deadline 1.png';
import renewIcon from '../../../../assets/img/renew-plan.png';
import {colors} from '../../../../assets/styles/colors';

import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';

import {actions as UiActions} from '../../../ui/duck';
import {actions as AccountActions} from '../../../profile/account/duck';
import type {AccountConfigAction} from '../../../profile/account/duck';
import type {User} from '../../../../entities';
import type {RootState} from '../../..';
import type {Dispatch} from 'redux';
import {useStoreInfo} from '../../../store/utils';

import {DOWNGRADE_TIERS} from '../../../../entities/storeTier';

import {updateLocalStorage, getLocalStorage} from '../../../../utils/dom';

export default connect(
  (state: RootState) => ({
    user: state.auth.user,
    isSaving: state.ui.isSavingDowngradeInformation,
    saveDowngradeInformationSuccess: state.ui.saveDowngradeInformationSuccess,
    saveDowngradeInformationError: state.ui.saveDowngradeInformationError,
    expiredSubscription:
      state.accountConfig.subscriptionSetting &&
      state.accountConfig.subscriptionSetting.expiredSubscription,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...UiActions,
        ...AccountActions,
      },
      dispatch
    )
)(PlanExpiredModal);

type Props = {
  user?: User,
  saveDowngradeInformation: ({
    saveDowngradeInformationRequest?: {[key: string]: string},
  }) => void,
  saveSubscriptionSetting: (payload: AccountConfigAction) => void,
  isSaving?: boolean,
  saveDowngradeInformationSuccess?: boolean,
  saveDowngradeInformationError?: boolean,
  expiredSubscription?: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 420,
    maxWidth: '100%',
    padding: theme.spacing(5, 2, 1, 2),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  subtitle: {
    marginTop: theme.spacing(2),
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '16px',
    color: colors.grey900,
    [theme.breakpoints.up('md')]: {
      fontSize: '18px',
    },
  },
  text: {
    fontWeight: 500,
    fontSize: '16px',
    marginTop: theme.spacing(3),
    color: colors.grey800,
    // fontSize: '1.125rem',
  },
  bold: {fontWeight: 600},
  link: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
  },
  wrapButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    margin: '0 auto',
    height: 48,
    // borderRadius: '8px',
    marginTop: theme.spacing(2),
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: '4px 80px',
    maxWidth: '100%',
  },
  secondButton: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    fontSize: '16px',
  },
  formContainer: {
    width: '100%',
  },
  inputWrap: {
    margin: theme.spacing(2, 0),
  },
  bigSelect: {
    paddingTop: '30px',
  },
  remember: {
    textAlign: 'center',
  },
}));

const optionsNotRenew = [
  'No vendo lo suficiente.',
  'Mi emprendimiento está en pausa.',
  'Mis clientes no me compran por la plataforma.',
  'Problemas con la plataforma.',
  'Necesito más funcionalidades.',
  'No me gustó TiendaDa.',
  'No pude usar la plataforma.',
  'Olvidé renovar pero lo haré.',
];

const optionsLikeMost = [
  'Facilidad de uso.',
  'Ahorro de tiempo.',
  'Contenido y talleres.',
  'Soporte del equipo.',
  'No me gustó.',
];

const MODAL_EXPIRED_SUBSCRIPTION = 'MODAL_EXPIRED_SUBSCRIPTION';

function PlanExpiredModal(props: Props) {
  const {
    user,
    saveSubscriptionSetting,
    saveDowngradeInformation,
    isSaving,
    saveDowngradeInformationSuccess,
    saveDowngradeInformationError,
  } = props;
  const classes = useStyles();
  const store = useStoreInfo();
  const {register, errors, handleSubmit} = useForm();

  const alreadyShowed = getLocalStorage(MODAL_EXPIRED_SUBSCRIPTION);

  const isDowngrade = DOWNGRADE_TIERS.includes(store?.storeTier?.name);

  const [modalOpen, setModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const submitForm = data => {
    const saveDowngradeInformationRequest = {
      ...data,
      suggestion: data.sugestion !== '' ? data.suggestion : undefined,
    };
    saveDowngradeInformation({saveDowngradeInformationRequest});
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    if (isDowngrade) {
      setModalOpen(true);
    }
  }, [isDowngrade]);

  useEffect(() => {
    if (saveDowngradeInformationSuccess || saveDowngradeInformationError) {
      if (saveDowngradeInformationSuccess) {
        saveSubscriptionSetting({
          accountConfigRequest: {expiredSubscription: false},
        });
        updateLocalStorage(MODAL_EXPIRED_SUBSCRIPTION, true);
        setModalOpen(false);
      }
      setAlertOpen(true);
    }
  }, [
    saveSubscriptionSetting,
    saveDowngradeInformationError,
    saveDowngradeInformationSuccess,
  ]);

  if (!isDowngrade || alreadyShowed) {
    return null;
  }

  return (
    <CustomModal open={modalOpen} handleCloseModal={handleClose}>
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert severity="success" onClose={() => setAlertOpen(false)}>
          Gracias por brindarnos tu opinión.
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!saveDowngradeInformationError}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpen(false)}>
          {saveDowngradeInformationError}
        </Alert>
      </Snackbar>
      <div className={classes.root}>
        <img src={renewIcon} alt="renew icon" />
        <Typography className={classes.title}>
          ¡Hola {user ? user.firstName : ''}!
        </Typography>
        <Typography className={classes.text} align="center">
          Tu suscripción ha terminado.
          <br /> Mes a mes seguimos lanzando nuevas funcionalidades que te
          ayudarán a crecer.
        </Typography>
        <Typography className={classes.subtitle}>
          Ayúdanos a entender tus necesidades respondiendo estas preguntas:
        </Typography>

        <form
          className={classes.formContainer}
          onSubmit={handleSubmit(submitForm)}
        >
          <div className={classes.inputWrap}>
            <CustomSelect
              classes={{root: classes.bigSelect}}
              native
              fullWidth
              label="¿Por qué razón decidiste NO renovar tu 
                suscripción PRO?"
              name="whyNotRenew"
              error={!!errors.whyNotRenew}
              helperText={errors.whyNotRenew ? errors.whyNotRenew.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              disabled={isSaving || saveDowngradeInformationSuccess}
            >
              <option value="" hidden />
              {optionsNotRenew &&
                optionsNotRenew.map((opt, idx) => (
                  <option key={`not-renew-option-${idx + 1}`} value={opt}>
                    {opt}
                  </option>
                ))}
            </CustomSelect>
          </div>
          <div className={classes.inputWrap}>
            <CustomSelect
              native
              fullWidth
              label="¿Qué es lo que más te gustó de TiendaDa?"
              name="whatLikedMost"
              error={!!errors.whatLikedMost}
              helperText={
                errors.whatLikedMost ? errors.whatLikedMost.message : ''
              }
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              disabled={isSaving || saveDowngradeInformationSuccess}
            >
              <option value="" hidden />
              {optionsLikeMost &&
                optionsLikeMost.map((opt, idx) => (
                  <option key={`like-most-option-${idx + 1}`} value={opt}>
                    {opt}
                  </option>
                ))}
            </CustomSelect>
          </div>
          <div className={classes.inputWrap}>
            <CustomTextField
              className={classes.categoryTextField}
              name="suggestion"
              error={!!errors.suggestion}
              helperText={errors.suggestion ? errors.suggestion.message : ''}
              inputRef={register()}
              type="text"
              placeholder="Tienes alguna recomendación? (Opcional)"
              disabled={isSaving || saveDowngradeInformationSuccess}
            />
          </div>
          {/*      <Typography className={classes.remember}>
            Recuerda que puedes seguir utilizando TiendaDa gratis y por tiempo
            indefinido.
          </Typography> */}
          <div className={classes.wrapButton}>
            <CustomButton
              type="submit"
              variant="contained"
              className={classes.button}
              disabled={isSaving || saveDowngradeInformationSuccess}
              // onClick={handleClickConfirm}
            >
              Enviar
            </CustomButton>
          </div>
        </form>
        {/* <Link
          className={classes.link}
          href={TIENDADA_LINKS.customerSupport.general}
          target="_blank"
          rel="noopener noreferrer"
        >
          Si tienes alguna duda,{' '}
          <span className={classes.bold}>contáctate con nosotros.</span>
        </Link> */}
        <CustomButton
          color="primary"
          component={Link}
          className={classes.secondButton}
          onClick={handleClose}
          to="/tiers-list"
        >
          Ir a renovar plan
        </CustomButton>
      </div>
    </CustomModal>
  );
}
